<template>
  <div class="rf-rc-settingss-left rf-rc-col1">
    <div class="promo-design-group flex flex-col gap-4">
      Transparency
      <v-color-picker
        v-model.trim="actions.rf_settings_overlay_background"
        canvasHeight="110"
        dotSize="30"
        class="window-transparency-picker mx-auto"
        variant="outlined"
      ></v-color-picker>
      Border
      <div class="promo-fonts-size-input">
        <v-select
          v-model="retentionCorner"
          :items="[all, topRight, topLeft, bottomRight, bottomLeft]"
          class="flex-1"
          label="Corner"
          itemTitle="text"
          density="compact"
          hideDetails
          variant="outlined"
          mandatory
          @update:modelValue="v => v === 'all' && updateAll()"
        />
        <RfInputWithMeasures
          v-model="variable"
          class="flex-1"
          :measures="WEB_MEASURES"
          label="Radius"
        />
      </div>
      <div class="promo-fonts-size-input">
        <RfInputWithMeasures
          v-model="borderWidth"
          class="flex-1 shrink grow-0"
          :measures="BORDER_MEASURES"
          label="Width"
        />
        <v-select
          v-model="props.actions[`${key}_border-style`]"
          :items="[
            'solid',
            'dashed',
            'dotted',
            'double',
            'groove',
            'ridge',
            'inset',
            'outset',
            'hidden',
          ]"
          class="shrink-0! grow!"
          label="Corner"
          density="compact"
          hideDetails
          variant="outlined"
          mandatory
        />
        <RfMenu hideIcon minFit preset="promptStatus" class="w-28 shrink-0 *:w-full">
          <template #activator>
            <v-text-field
              v-model="props.actions[`${key}_border-color`]"
              persistentPlaceholder
              variant="outlined"
              density="compact"
              hideDetails
              style="margin-left: 10px"
              label="Color"
              @blur="
                props.actions[`${key}_border-color`] = extendHex(
                  props.actions[`${key}_border-color`],
                )
              "
            >
            </v-text-field>
          </template>
          <template #menu>
            <v-color-picker
              v-model.trim="props.actions[`${key}_border-color`]"
              canvasHeight="110"
              dotSize="30"
              class="window-transparency-picker w-72"
              variant="outlined"
            ></v-color-picker>
          </template>
        </RfMenu>
      </div>
      <RfSwitch
        class="mr-auto ml-0 flex-row-reverse"
        :modelValue="props.actions['rf_retention_box-sizing'] === 'border-box'"
        @update:modelValue="
          v => (props.actions['rf_retention_box-sizing'] = v ? 'border-box' : 'content-box')
        "
      >
        <template #title>
          <span class="inline-flex items-center gap-2">
            <span>Toggle border-box sizing</span>
            <RfTooltip class="grow-0">
              <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
              <template #tooltip>
                <div class="w-64">
                  This toggle will change the box-sizing of the element to border-box, which means
                  that the width and height of the element will include padding and border, but not
                  margin. This may help if the width of your prompt exceeds the limits of it
                </div>
              </template>
            </RfTooltip>
          </span>
        </template>
      </RfSwitch>
      Shadow
      <div class="promo-fonts-size-input">
        <RfInputWithMeasures v-model="boxShadowX" :measures="BORDER_MEASURES" label="x" />
        <RfInputWithMeasures v-model="boxShadowY" :measures="BORDER_MEASURES" label="y" />
      </div>
      <div class="promo-fonts-size-input">
        <RfInputWithMeasures v-model="boxShadowBlur" :measures="BORDER_MEASURES" label="Blur" />
        <RfInputWithMeasures v-model="boxShadowSpread" :measures="BORDER_MEASURES" label="Spread" />

        <RfMenu hideIcon minFit preset="promptStatus" class="w-28 shrink-0 *:w-full">
          <template #activator>
            <v-text-field
              v-model="boxShadow.color"
              persistentPlaceholder
              variant="outlined"
              density="compact"
              hideDetails
              style="margin-left: 10px"
              label="Color"
              @blur="boxShadow.color = extendHex(boxShadow.color)"
            >
            </v-text-field>
          </template>
          <template #menu>
            <v-color-picker
              v-model.trim="boxShadow.color"
              canvasHeight="110"
              dotSize="30"
              class="window-transparency-picker w-72"
              variant="outlined"
            ></v-color-picker>
          </template>
        </RfMenu>
      </div>
    </div>
  </div>
</template>

<script setup>
import { BORDER_MEASURES, useCSSProperty, WEB_MEASURES } from "@/utils/composables/useCSSProperty";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { bottomLeft, bottomRight, topLeft, topRight } from "@/utils/constants/PromptStyleConstants";
import RfMenu from "@/components/menus/RfMenu.vue";
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfInputWithMeasures from "@/components/legacy/RfInputWithMeasuresLegacy.vue";
import { extendHex } from "@/utils/colorHelpers";

const all = { value: "all", text: "All" };

const props = defineProps({
  actions: { type: Object, default: () => ({}) },
  mobile: { type: Boolean, default: false },
});

const retentionCorner = ref("all");
const key = computed(() => (props.mobile ? "rf_mobile" : "rf_retention"));

const { variable: topRightCorner } = useCSSProperty(
  () => props.actions,
  `${key.value}_border-${topRight.value}-radius`,
);
const { variable: topLeftCorner, init: topLeftCornerInit } = useCSSProperty(
  () => props.actions,
  `${key.value}_border-${topLeft.value}-radius`,
);
const { variable: bottomRightCorner, init: bottomRightCornerInit } = useCSSProperty(
  () => props.actions,
  `${key.value}_border-${bottomRight.value}-radius`,
);
const { variable: bottomLeftCorner, init: bottomLeftCornerInit } = useCSSProperty(
  () => props.actions,
  `${key.value}_border-${bottomLeft.value}-radius`,
);

const cornerMap = computed(() => ({
  [topRight.value]: topRightCorner,
  [topLeft.value]: topLeftCorner,
  [bottomRight.value]: bottomRightCorner,
  [bottomLeft.value]: bottomLeftCorner,
}));

const updateAll = () => {
  topLeftCornerInit(topRightCorner.value);
  bottomRightCornerInit(topRightCorner.value);
  bottomLeftCornerInit(topRightCorner.value);
};

const variable = computed({
  get() {
    if (retentionCorner.value === "all") {
      updateAll();
      return topRightCorner.value;
    }
    return cornerMap.value[retentionCorner.value].value;
  },
  set(v) {
    if (retentionCorner.value === "all") {
      topRightCorner.value = v;
      updateAll();
    } else {
      cornerMap.value[retentionCorner.value].value = v;
    }
  },
});

const { variable: borderWidth } = useCSSProperty(() => props.actions, `${key.value}_border-width`);

const [x = "0px", y = "0px", blur = "0px", spread = "0px", color = "#ffffff"] = props.actions[
  `${key.value}_box-shadow`
]
  ? props.actions[`${key.value}_box-shadow`].split(" ")
  : ["0px", "0px", "0px", "0px", "#ffffff"];
const boxShadow = reactive({ x, y, blur, spread, color });
const { variable: boxShadowX } = useCSSProperty(() => boxShadow, "x");
const { variable: boxShadowY } = useCSSProperty(() => boxShadow, "y");
const { variable: boxShadowBlur } = useCSSProperty(() => boxShadow, "blur");
const { variable: boxShadowSpread } = useCSSProperty(() => boxShadow, "spread");

watch(
  boxShadow,
  _v =>
    (props.actions[`${key.value}_box-shadow`] =
      `${boxShadow.x} ${boxShadow.y} ${boxShadow.blur} ${boxShadow.spread} ${boxShadow.color}`),
  { deep: true },
);

onMounted(() => {
  if (!props.actions[`${key.value}_border-style`]) {
    props.actions[`${key.value}_border-style`] = "hidden";
  }
  if (!props.actions[`${key.value}_border-color`]) {
    props.actions[`${key.value}_border-color`] = "#000000";
  }
  if (!props.actions.rf_settings_overlay_background) {
    props.actions.rf_settings_overlay_background = "#252323B3";
  }
});
</script>
